import config from "@/modules/config";
import * as analytics from "@/modules/analytics";
import ChildMessenger from "@/modules/ChildMessenger";
import * as sizeManager from "@/modules/sizeManager";
import * as orientationManager from "@/modules/orientationManager";
import { orientationType } from "@/modules/orientationManager";
import { constants, shopWindowTypes, valueNames } from "@/modules/Constants";
import * as elementUtils from "@/common/utils/elementUtils";
import { resize } from "@/stores/experience/actions/layout";
import { fetchExperienceData } from "@/stores/experience/actions/loading";
import { prefetchChain } from "@/stores/chain/actions";
import { pauseOverlayPlayback } from "@/modules/overlayPlayback";
import * as watchTimeTracker from "@/modules/WatchTimeTracker";
import * as chainSession from "@/modules/ChainSession";
import { buildExperienceUrlForId } from "@/modules/chainAdvance";
import { registerChainDepth } from "@/modules/mixpanelBootstrap";
import { isImmersiveLandscapeSize } from "@/modules/orientationManager";
import { isMcpLandscapeLayout } from "@/modules/mcpSurface";
import { releasePixiePortraitLock } from "@/modules/pixieWorkspace";
import type { MessengerCallback } from "@/common/Messenger";
import type { ExperienceState } from "@/types/state";
import AppShopWindow, { type ShopWindowValue } from "@/modules/appShopWindow";

class AppController extends AppShopWindow {
  declare childMessenger: ChildMessenger | undefined;
  declare _region: string;
  declare _unloadFired: boolean;

  constructor() {
    super();
    this._onPageHide = this._onPageHide.bind(this);
    this._onVisibilityChange = this._onVisibilityChange.bind(this);
    this._onResize = this._onResize.bind(this);
    this._onOrientationChanged = this._onOrientationChanged.bind(this);
    this._onParentResize = this._onParentResize.bind(this);
    this._region = "App Controller";
    this._unloadFired = false;
    // Set while the portrait chat overlay has auto-paused the video,
    // so it is resumed on close only when the viewer had it playing.
    this._chatAutoPaused = false;
  }

  activateApplication() {
    window.onerror = function myErrorHandler(errorMsg, url, lineNumber) {
      console.error(`Error occured: ${errorMsg}`);
      return false;
    };

    const metadata = this.state.experienceData.metadata;
    const isIframe = elementUtils.isIFrame();
    /*
     * Messenger skips postMessage when targetOrigin is falsy.
     * Do not use '*' — embed contract requires known pt origin for parent messaging.
     */
    const targetOrigin = config.targetOrigin();

    /*
     * Prefetch listener + waitForParentPrefetch waiter must exist before
     * reportInitialized — parent overlay replies immediately and drops
     * postMessage if listener not installed yet.
     */
    fetchExperienceData(
      "App Controller",
      metadata.idexperience,
      metadata.experienceUrl,
    );

    // Kick off the chain prefetch at boot, in parallel with the
    // experience-data fetch above — the chain request only needs the id, which
    // we already have here. Without this it wouldn't fire until the video's
    // loadstart (seconds later, after the experience load + player mount), so the
    // next arrow stayed disabled well into playback. Deduped in prefetchChain, so
    // VideoJs's loadstart/media-ready triggers become no-ops for this experience.
    if (metadata && metadata.idexperience != null) {
      prefetchChain(metadata.idexperience);
    }

    if (isIframe && targetOrigin) {
      this.childMessenger = new ChildMessenger(
        window,
        window.parent,
        targetOrigin,
      );
      this.childMessenger.setName("app");
      this.childMessenger.setPrefix(constants.MESSAGE_PREFIX);
      this.childMessenger.onMessengerHandler = {
        onResize: this._onParentResize as MessengerCallback,
      };
      this.childMessenger.startListening();
      this.childMessenger.reportInitialized();
    }

    sizeManager.addListener(this._onResize);
    orientationManager.addListener(this._onOrientationChanged);
    window.addEventListener("pagehide", this._onPageHide);
    document.addEventListener("visibilitychange", this._onVisibilityChange);
  }

  deactivateApplication() {
    if (this.childMessenger) {
      this.childMessenger.stopListening();
    }
    sizeManager.removeListener(this._onResize);
    orientationManager.removeListener(this._onOrientationChanged);
    window.removeEventListener("pagehide", this._onPageHide);
    document.removeEventListener("visibilitychange", this._onVisibilityChange);
    this._unloadFired = false;
  }

  setState(state: ExperienceState) {
    this.state = state;
  }

  reportMediaReady() {
    if (this.childMessenger) {
      this.childMessenger.reportMediaReady();
    }
  }

  _onPageHide() {
    this._reportUnload("page_exit");
  }

  _onVisibilityChange() {
    if (document.visibilityState === "hidden") {
      this._reportUnload("page_exit");
    } else if (document.visibilityState === "visible") {
      this._unloadFired = false;
    }
  }

  // reason: 'page_exit' (pagehide / tab hidden — page is dying, use sendBeacon)
  // or 'chain_advance' (mid-video in-place switch — page stays alive). Carries the
  // leaving experience's watch time so each chain experience accrues its own minutes.
  _reportUnload(reason: string) {
    if (this._unloadFired) {
      return;
    }
    this._unloadFired = true;
    watchTimeTracker.pause();
    const options =
      reason === "chain_advance"
        ? undefined
        : { transport: "sendBeacon" as const };
    analytics.track(
      "Experience Unload",
      {
        pix_offset: this.state.experienceData.nowTime,
        pix_watch_time: watchTimeTracker.getSeconds(),
        pix_unload_reason: reason,
      },
      options,
    );
  }

  _onResize() {
    resize();
  }

  _onOrientationChanged() {
    // Count a real rotation only — a mobile portrait → landscape flip.
    // Two guards:
    //   • device is mobile — orientation here is derived from window aspect
    //     (height vs width, see orientationManager.calcOrientation), so on
    //     desktop a browser RESIZE across the square boundary flips it with no
    //     physical rotation. Only a mobile aspect flip is a genuine rotation.
    //   • now landscape — orientationManager is binary and only notifies on an
    //     actual change, so "now landscape" is exactly a portrait → landscape
    //     transition (previously fired on every change, double-counting the way
    //     back too).
    if (
      config.device === "mobile" &&
      orientationManager.getOrientation() === orientationType.LANDSCAPE
    ) {
      analytics.track("Screen Rotation", {
        pix_offset: this.state.experienceData.nowTime,
      });
    }
    this.setApplicationValue(
      this._region,
      valueNames.ORIENTATION,
      orientationManager.getOrientation(),
    );

    // Re-sync the chat auto-pause on rotation so the pause state stays correct
    // across an orientation change. Pixie now pauses the video on every surface,
    // so a rotation while Pixie is open keeps it paused (and a rotation onto any
    // other tab resumes it) -- the decision is otherwise only evaluated on tab
    // selection (setShopWindow), not here where orientation changes.
    this._syncChatAutoPause(
      this.state.applicationData.getValue(
        valueNames.SHOP_WINDOW_CONFIG,
      ) as ShopWindowValue,
    );
  }

  _isAncestorWindow(w: MessageEventSource | null): boolean | undefined {
    let swin: Window = window;
    if (w === swin) {
      return true;
    }
    while (swin !== swin.parent) {
      swin = swin.parent;
      if (w === swin) {
        return true;
      }
    }
  }

  _onParentResize(
    messenger: ChildMessenger,
    event: MessageEvent,
    data: { width: number; height: number },
  ) {
    if (this._isAncestorWindow(event.source)) {
      sizeManager.monitorWindow(false);
      sizeManager.setSize(data);
    } else {
      console.warn("Invalid parent for resize!");
    }
  }

  exit(region: string) {
    if (this.childMessenger) {
      pauseOverlayPlayback();
      watchTimeTracker.pause();
      analytics.track(
        "Exit",
        {
          pix_appregion: region,
          pix_offset: this.state.experienceData.nowTime,
          pix_watch_time: watchTimeTracker.getSeconds(),
        },
        { transport: "sendBeacon" },
      );
      this.childMessenger.reportExit();
    } else if (config.isWindow()) {
      window.close();
    } else if (config.isPage()) {
      window.history.back();
    }
  }

  // In-place advance to a chain recommendation. Reuses the normal load
  // path (fetchExperienceData → setExperienceData → VideoJs id-diff swaps the
  // video), so no page reload and the session continues. Records the video we
  // are leaving first, so the next prefetch excludes it — VIDEO_ENDED only fires
  // on a natural end, but a manual Play now can advance before that.
  advanceToChainExperience(idexperience: unknown) {
    if (idexperience == null) {
      return;
    }
    // The advance rebuilds the tabs without going through setShopWindow, so
    // leave Pixie's pause and portrait lock here: the next video plays.
    this._chatAutoPaused = false;
    releasePixiePortraitLock();
    // Landscape phone and the MCP landscape card: a recipe picked in Pixie
    // plays on the bare video. Pixie holds the overlay sheet open, and the
    // rebuilt tabs would otherwise show the next recipe's Method sheet over it.
    const applicationData = this.state && this.state.applicationData;
    const openWindow = applicationData
      ? (applicationData.getValue(
          valueNames.SHOP_WINDOW_CONFIG,
        ) as ShopWindowValue)
      : null;
    if (
      openWindow &&
      openWindow.type === shopWindowTypes.ASK &&
      applicationData.isLandscape() &&
      (isImmersiveLandscapeSize() || isMcpLandscapeLayout())
    ) {
      this.closeShopWindow("Chain advance");
    }
    const url = buildExperienceUrlForId(idexperience);
    if (!url) {
      console.warn(
        "[chain] advance skipped: no experience URL for",
        idexperience,
      );
      return;
    }
    // Record the CURRENT experience from the live store — NOT
    // window.pixmoto.experienceMetadata, which player-boot sets once at initial
    // load and never updates on in-place swaps (so it would always record the
    // first video, dropping every id advanced through in between).
    const current =
      this.state && this.state.experienceData
        ? this.state.experienceData.idexperience
        : null;
    // Capture the leaving experience's watch time when advancing MID-VIDEO. An
    // ended video already reported it via Video Ended (auto-advance and Up Next
    // only fire post-end), so guarding on !ended avoids duplicating the same
    // watch-time goal. Fire before recordWatched/registerChainDepth so the event
    // carries the leaving experience's id, bundle, and (un-bumped) chain depth.
    const ended =
      this.state && this.state.applicationData
        ? this.state.applicationData.getValue(valueNames.VIDEO_ENDED, false)
        : false;
    if (!ended && current != null) {
      this._reportUnload("chain_advance");
      this._unloadFired = false; // allow the incoming experience to unload later
    }
    if (current != null) {
      const currentTitle =
        this.state && this.state.experienceData
          ? this.state.experienceData.title
          : null;
      chainSession.recordWatched(current, currentTitle);
    }
    // The incoming experience is the next chain position: depth() now counts the
    // leaving video just recorded, so its 1-based position is depth() + 1. Re-
    // register here (a new experience becomes current) — never in handleVideoEnded,
    // where the current video is unchanged.
    registerChainDepth(chainSession.depth() + 1);
    // Continue playback into the chained video (playlist-style). The
    // session already has a user gesture (they were watching), so VideoJs plays
    // the swapped-in video on load even if that experience's autoPlay is off.
    // Go through setApplicationValue (not a raw setValue) for the same
    // willSetValue/didSetValue plugin hooks every other write here uses; safe
    // because advance always runs outside a dispatch (deferred timer / onClick).
    if (this.state && this.state.applicationData) {
      this.setApplicationValue(
        "Chain advance",
        valueNames.CHAIN_AUTOPLAY,
        true,
      );
    }
    // Prefetch the *incoming* video's chain now, in parallel with its
    // experience fetch below — not at its loadstart (which is after the swap
    // loads + the player re-mounts, leaving the next arrow disabled 2-3s into the
    // new video). chainSession already has the leaving video recorded above, so
    // the request carries the correct history. Deduped, so the incoming video's
    // loadstart is a no-op for it.
    prefetchChain(idexperience);
    fetchExperienceData("Chain advance", idexperience, url);
  }
}

export default new AppController();
